'use client';

import React, { useEffect, useRef, useState, useMemo } from 'react';
import { motion, useInView, useMotionValue, useSpring, useTransform } from 'framer-motion';
import AppImage from '@/components/ui/AppImage';
import ProductJsonLd from '@/components/seo/ProductJsonLd';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight, X, Play } from 'lucide-react'; // Added lucide-react icons
import { FaAngleDown } from 'react-icons/fa6';
import { SITE_URL } from '@/lib/siteUrl';
import {
  trackAddPaymentInfo,
  trackAddToCart,
  trackBeginCheckout,
  trackPurchase,
  trackViewItem,
} from '@/utils/analytics/ecommerce';

type Variant = {
  id: string;
  name: string;
  price: number;
  mrpPrice?: number;
  discountPercentage?: number;
  stock: number;
  images?: string[]; // Added images array to support variant-specific images
  weightKg: number;
  lengthCm: number;
  breadthCm: number;
  heightCm: number;
};

type CheckoutForm = {
  customerName: string;
  mobileNumber: string;
  email: string;
  addressLine1: string;
  addressLine2: string;
  landmark: string;
  city: string;
  state: string;
  pincode: string;
  country: string;
};

type PaymentMethod = 'prepaid' | 'cod';

type CheckoutStep = 1 | 2 | 3;

type PincodeDetails = {
  pincode: string;
  city: string;
  locality: string[];
  state: string;
  stateCode?: string;
  longitude?: string;
  latitude?: string;
  country: string;
};

type ShippingQuote = {
  available: boolean;
  shippingCharge: number;
  freeDelivery: boolean;
  eta: string | null;
  courierName: string | null;
  pincode: string;
  paymentMethod: PaymentMethod;
  variantId: string;
  quantity: number;
  city: string;
  state: string;
  country: string;
  pincodeDetails: PincodeDetails | null;
};

type OrderReceipt = {
  orderId: string;
  orderNumber: string;
  amount: number;
  shippingCharge: number;
  paymentMethod: PaymentMethod;
  eta: string | null;
};

type PaymentSession = {
  orderId: string;
  orderNumber: string;
  amount: number;
  amountPaise: number;
  currency: string;
  razorpayOrderId: string;
  razorpayKeyId: string;
};

declare global {
  interface Window {
    Razorpay?: new (options: Record<string, unknown>) => {
      open: () => void;
      on: (event: string, callback: (response: { error?: { description?: string } }) => void) => void;
    };
  }
}

type MediaItem = {
  id: string | number;
  type: 'image' | 'video';
  src: string;
  alt: string;
};

const PRODUCT_VIDEO_URL = 'https://www.youtube.com/embed/PMEKBm0W29k?si=Cor89up00P3mkaj5';

const productVideoMedia: MediaItem = {
  id: 'product-video',
  type: 'video',
  src: PRODUCT_VIDEO_URL,
  alt: 'Dr. Detoxer Product Video',
};

const apiBaseUrl = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:5001/api';
const publicRazorpayKeyId = process.env.NEXT_PUBLIC_RAZORPAY_KEY_ID || '';
const checkoutStorageKey = 'drdetoxer_checkout_profile_v1';
const paymentMethodStorageKey = 'drdetoxer_checkout_payment_method_v1';
const quoteStorageKey = 'drdetoxer_checkout_quote_v1';
const PINCODE_REGEX = /^[1-9]\d{5}$/;
const MOBILE_REGEX = /^[6-9]\d{9}$/;
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const initialCheckoutForm: CheckoutForm = {
  customerName: '',
  mobileNumber: '',
  email: '',
  addressLine1: '',
  addressLine2: '',
  landmark: '',
  city: '',
  state: '',
  pincode: '',
  country: 'India',
};

function getStoredCheckoutForm(): CheckoutForm | null {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    const raw = window.localStorage.getItem(checkoutStorageKey);

    if (!raw) {
      return null;
    }

    const parsed = JSON.parse(raw) as Partial<CheckoutForm>;

    return {
      customerName: typeof parsed.customerName === 'string' ? parsed.customerName : '',
      mobileNumber: typeof parsed.mobileNumber === 'string' ? parsed.mobileNumber : '',
      email: typeof parsed.email === 'string' ? parsed.email : '',
      addressLine1: typeof parsed.addressLine1 === 'string' ? parsed.addressLine1 : '',
      addressLine2: typeof parsed.addressLine2 === 'string' ? parsed.addressLine2 : '',
      landmark: typeof parsed.landmark === 'string' ? parsed.landmark : '',
      city: typeof parsed.city === 'string' ? parsed.city : '',
      state: typeof parsed.state === 'string' ? parsed.state : '',
      pincode: typeof parsed.pincode === 'string' ? parsed.pincode : '',
      country: typeof parsed.country === 'string' && parsed.country ? parsed.country : 'India',
    };
  } catch {
    return null;
  }
}

function normalizePincodeDetails(payload: unknown): PincodeDetails | null {
  if (!payload || typeof payload !== 'object') return null;

  const source = payload as Partial<PincodeDetails>;
  const pincode = typeof source.pincode === 'string' ? source.pincode.trim() : '';
  const city = typeof source.city === 'string' ? source.city.trim() : '';
  const state = typeof source.state === 'string' ? source.state.trim() : '';
  const country = typeof source.country === 'string' && source.country.trim()
    ? source.country.trim()
    : 'India';

  if (!city || !state) return null;

  return {
    pincode,
    city,
    locality: Array.isArray(source.locality)
      ? source.locality.filter((item): item is string => typeof item === 'string' && item.trim().length > 0)
      : [],
    state,
    stateCode: typeof source.stateCode === 'string' ? source.stateCode : '',
    longitude: typeof source.longitude === 'string' ? source.longitude : '',
    latitude: typeof source.latitude === 'string' ? source.latitude : '',
    country,
  };
}

function getStoredPaymentMethod(): PaymentMethod {
  if (typeof window === 'undefined') return 'prepaid';
  const raw = window.localStorage.getItem(paymentMethodStorageKey);
  return raw === 'cod' ? 'cod' : 'prepaid';
}

function getStoredShippingQuote(): ShippingQuote | null {
  if (typeof window === 'undefined') return null;

  try {
    const raw = window.localStorage.getItem(quoteStorageKey);
    if (!raw) return null;

    const parsed = JSON.parse(raw) as Partial<ShippingQuote>;
    const parsedQuantity = Number(parsed.quantity);
    if (
      typeof parsed.pincode !== 'string' ||
      !PINCODE_REGEX.test(parsed.pincode) ||
      (parsed.paymentMethod !== 'prepaid' && parsed.paymentMethod !== 'cod') ||
      typeof parsed.variantId !== 'string' ||
      !parsed.variantId ||
      !Number.isInteger(parsedQuantity) ||
      parsedQuantity < 1
    ) {
      return null;
    }

    return {
      available: Boolean(parsed.available),
      shippingCharge: Number(parsed.shippingCharge) || 0,
      freeDelivery: Boolean(parsed.freeDelivery),
      eta: typeof parsed.eta === 'string' ? parsed.eta : null,
      courierName: typeof parsed.courierName === 'string' ? parsed.courierName : null,
      pincode: parsed.pincode,
      paymentMethod: parsed.paymentMethod,
      variantId: parsed.variantId,
      quantity: parsedQuantity,
      city: typeof parsed.city === 'string' ? parsed.city : '',
      state: typeof parsed.state === 'string' ? parsed.state : '',
      country: typeof parsed.country === 'string' && parsed.country ? parsed.country : 'India',
      pincodeDetails: normalizePincodeDetails(parsed.pincodeDetails),
    };
  } catch {
    return null;
  }
}

function loadRazorpayScript() {
  if (typeof window === 'undefined') {
    return Promise.resolve(false);
  }

  if (window.Razorpay) {
    return Promise.resolve(true);
  }

  return new Promise<boolean>((resolve) => {
    const existingScript = document.querySelector('script[data-razorpay-checkout="true"]') as HTMLScriptElement | null;

    if (existingScript) {
      existingScript.addEventListener('load', () => resolve(true), { once: true });
      existingScript.addEventListener('error', () => resolve(false), { once: true });
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.async = true;
    script.dataset.razorpayCheckout = 'true';
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
}

const defaultVariants = [
  // { id: 'default-440ml', name: '440ml', price: 185, mrpPrice: 370, discountPercentage: 50, stock: 500 },
  {
    id: 'default-880ml',
    name: '880ml',
    price: 349,
    mrpPrice: 698,
    discountPercentage: 50,
    stock: 500,
    weightKg: 0.9,
    lengthCm: 20,
    breadthCm: 15,
    heightCm: 16,
  },
  // { id: 'default-5000ml', name: '5000ml', price: 1400, mrpPrice: 3500, discountPercentage: 60, stock: 500 }
];

export default function ProductShowcase() {
  const { t } = useTranslation(['home', 'common']);
  const ref = useRef(null);
  const imageRef = useRef<HTMLDivElement>(null);
  const submitLockRef = useRef(false);
  const trackedViewVariantRef = useRef<string | null>(null);
  const inView = useInView(ref, { once: true, margin: '-80px' });
  const [quantity, setQuantity] = useState(1);
  const [variants, setVariants] = useState<Variant[]>(defaultVariants);
  const [selectedVariantId, setSelectedVariantId] = useState('default-880ml');
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [checkoutForm, setCheckoutForm] = useState<CheckoutForm>(initialCheckoutForm);
  const [isSubmittingOrder, setIsSubmittingOrder] = useState(false);
  const [checkoutError, setCheckoutError] = useState('');
  const [orderReceipt, setOrderReceipt] = useState<OrderReceipt | null>(null);
  const [checkoutStep, setCheckoutStep] = useState<CheckoutStep>(1);
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>('prepaid');
  const [shippingQuote, setShippingQuote] = useState<ShippingQuote | null>(null);
  const [isQuoteLoading, setIsQuoteLoading] = useState(false);
  const [quoteError, setQuoteError] = useState('');
  const [selectedMrpPrice, setSelectedMrpPrice] = useState(variants[0].mrpPrice || 0);
  const [selectedDiscountPercentage, setSelectedDiscountPercentage] = useState(variants[0].discountPercentage || 0);

  // --- New Slider State ---
  const [currentIndex, setCurrentIndex] = useState<number>(0);
  const thumbnailCount = 5;

  const defaultImages = useMemo(() => [
    { id: 1, src: '/assets/images/only_bottel.png', alt: 'Dr. Detoxer Product 1' },
    { id: 2, src: '/assets/images/product_details_1.jpeg', alt: 'Product Image 2' },
    { id: 3, src: '/assets/images/product_details_2.jpeg', alt: 'Why Us Image' },
    { id: 4, src: '/assets/images/product_details_3.jpeg', alt: 'Product Image 4' },
    { id: 5, src: '/assets/images/product_details_4.jpeg', alt: 'Product Image 5' },
    { id: 6, src: '/assets/images/product_details_5.jpeg', alt: 'Product Image 6' },
    { id: 7, src: '/assets/images/product_details_6.jpeg', alt: 'Product Image 7' },
    { id: 8, src: '/assets/images/product_details_7.jpeg', alt: 'Product Image 8' },
  ], []);

  const selectedVariant = variants.find((variant) => variant.id === selectedVariantId) || variants[0];

  // Compute images based on the selected variant
  const currentImages = useMemo(() => {
    if (selectedVariant?.images && selectedVariant.images.length > 0) {
      return selectedVariant.images.map((imgSrc, idx) => ({
        id: idx + 1,
        src: imgSrc,
        alt: `${selectedVariant.name} Image ${idx + 1}`
      }));
    }
    return defaultImages;
  }, [selectedVariant, defaultImages]);

  // Build the unified media list — video always pinned at index 0, then any
  // variant images (or default images) follow.
  const currentMedia = useMemo<MediaItem[]>(() => {
    const imageItems: MediaItem[] = currentImages.map((img) => ({
      id: img.id,
      type: 'image',
      src: img.src,
      alt: img.alt,
    }));
    return [productVideoMedia, ...imageItems];
  }, [currentImages]);

  const [mainMedia, setMainMedia] = useState<MediaItem>(productVideoMedia);
  const [isMediaModalOpen, setIsMediaModalOpen] = useState(false);

  // Reset main media and index when the variant (and thus currentMedia) changes
  useEffect(() => {
    if (currentMedia.length > 0) {
      setMainMedia(currentMedia[0]);
      setCurrentIndex(0);
    }
  }, [currentMedia]);

  const handleNext = () => {
    if (currentIndex < currentMedia.length - thumbnailCount) {
      setCurrentIndex(currentIndex + 1);
    }
  };

  const handlePrev = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    }
  };
  // --- End Slider State ---

  useEffect(() => {
    setSelectedMrpPrice(selectedVariant?.mrpPrice || 0);
    setSelectedDiscountPercentage(selectedVariant?.discountPercentage || 0);
  }, [selectedVariant]);

  useEffect(() => {
    if (!shippingQuote) return;

    if (
      shippingQuote.variantId === selectedVariant?.id &&
      shippingQuote.quantity === quantity
    ) {
      return;
    }

    setShippingQuote(null);
    setQuoteError('');

    if (typeof window !== 'undefined') {
      window.localStorage.removeItem(quoteStorageKey);
    }
  }, [selectedVariant?.id, quantity, shippingQuote]);

  // Lock body scroll when cart or media modal is open
  useEffect(() => {
    if (isCartOpen || isMediaModalOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isCartOpen, isMediaModalOpen]);

  useEffect(() => {
    async function loadVariants() {
      try {
        const response = await fetch(`${apiBaseUrl}/products/landing`);
        const payload = await response.json();

        if (!response.ok || !Array.isArray(payload?.data?.variants) || payload.data.variants.length === 0) {
          return;
        }

        const withDiscount = payload.data.variants.map((variant: any) => {
          const name = typeof variant.name === 'string' ? variant.name : 'Variant';
          let mrpPrice, discountPercentage;
          const price = variant.price;
          let fallbackPackage = { weightKg: 0.5, lengthCm: 15, breadthCm: 10, heightCm: 10 };

          if(name.toLowerCase().includes('440')) {
            mrpPrice = 370;
            discountPercentage = 50;
            fallbackPackage = { weightKg: 0.5, lengthCm: 15, breadthCm: 10, heightCm: 10 };
          } else if(name.toLowerCase().includes('880')) {
            mrpPrice = 698;
            discountPercentage = 50;
            fallbackPackage = { weightKg: 0.9, lengthCm: 20, breadthCm: 15, heightCm: 16 };
          } else if(name.toLowerCase().includes('5000') || name.toLowerCase().includes('5l')) {
            mrpPrice = 3500;
            discountPercentage = 60;
            fallbackPackage = { weightKg: 5.1, lengthCm: 30, breadthCm: 30, heightCm: 30 };
          } else {
            mrpPrice = variant.price + variant.price * 0.5; // Default to 50% markup if variant name doesn't match expected patterns
            discountPercentage = 50;
          }

          const weightKg = Number(variant.weightKg);
          const lengthCm = Number(variant.lengthCm);
          const breadthCm = Number(variant.breadthCm);
          const heightCm = Number(variant.heightCm);

          const baseImageSources = currentImages.map((img) => img.src);
          const variantImageSources = Array.isArray(variant.images)
            ? variant.images
                .map((img: any) => (typeof img === 'string' ? img : img?.src))
                .filter((img: unknown) => typeof img === 'string')
            : [];

          return {
            ...variant,
            name,
            price,
            mrpPrice,
            discountPercentage,
            images: [...baseImageSources, ...variantImageSources],
            weightKg: Number.isFinite(weightKg) && weightKg > 0 ? weightKg : fallbackPackage.weightKg,
            lengthCm: Number.isFinite(lengthCm) && lengthCm > 0 ? lengthCm : fallbackPackage.lengthCm,
            breadthCm: Number.isFinite(breadthCm) && breadthCm > 0 ? breadthCm : fallbackPackage.breadthCm,
            heightCm: Number.isFinite(heightCm) && heightCm > 0 ? heightCm : fallbackPackage.heightCm,
          };
        });

        setVariants(withDiscount);
        setSelectedVariantId(withDiscount[0].id);
        setSelectedMrpPrice(withDiscount[0].mrpPrice);
        setSelectedDiscountPercentage(withDiscount[0].discountPercentage);
      } catch {
        // Keep fallback variant for resilient landing-page rendering
      }
    }

    loadVariants();
  }, []);

  useEffect(() => {
    setPaymentMethod(getStoredPaymentMethod());

    const storedQuote = getStoredShippingQuote();
    if (storedQuote) setShippingQuote(storedQuote);

    const storedCheckoutForm = getStoredCheckoutForm();

    if (storedCheckoutForm) {
      setCheckoutForm(storedCheckoutForm);

      if (storedCheckoutForm.mobileNumber) {
        const recoverState = async () => {
          try {
            const res = await fetch(`${apiBaseUrl}/orders/track?type=phone&query=${storedCheckoutForm.mobileNumber}&limit=1`);
            const payload = await res.json();
            const latestOrder = payload?.data?.items?.[0];

            if (latestOrder && latestOrder.paymentStatus === 'paid') {
              const orderTime = new Date(latestOrder.createdAt).getTime();
              const now = new Date().getTime();
              const fifteenMinutes = 15 * 60 * 1000;

              if (now - orderTime < fifteenMinutes) {
                setOrderReceipt({
                  orderId: latestOrder.id,
                  orderNumber: latestOrder.orderNumber,
                  amount: latestOrder.totalAmount,
                  shippingCharge: Number(latestOrder.shippingCharge) || 0,
                  paymentMethod: latestOrder.paymentMethod === 'cod' ? 'cod' : 'prepaid',
                  eta: typeof latestOrder.deliveryEta === 'string' ? latestOrder.deliveryEta : null,
                });
                setIsCartOpen(true);
              }
            }
          } catch {
            // Passive recovery fail is fine
          }
        };

        recoverState();
      }
    }
  }, []);

  const selectedPrice = selectedVariant?.price || 299;
  const totalPrice = selectedPrice * quantity;

  const highlights = [
    { icon: '🌿', text: t('product.highlights.naturalFormula') },
    { icon: '☠️', text: t('product.highlights.pesticideRemoval') },
    { icon: '🦠', text: t('product.highlights.killsBacteria') },
    { icon: '💧', text: t('product.highlights.safeDailyUse') },
    { icon: '🧴', text: t('product.highlights.bottle') },
    { icon: '🇮🇳', text: t('product.highlights.madeInIndia') }
  ];

  function updateFormField(field: keyof CheckoutForm, value: string) {
    setCheckoutForm((prev) => {
      const next = { ...prev, [field]: value };
      if (typeof window !== 'undefined') {
        window.localStorage.setItem(checkoutStorageKey, JSON.stringify(next));
      }
      return next;
    });

    // Invalidate the cached quote whenever the pincode changes — the user
    // must re-confirm serviceability before they can advance.
    if (field === 'pincode') {
      setShippingQuote(null);
      setQuoteError('');
      if (typeof window !== 'undefined') {
        window.localStorage.removeItem(quoteStorageKey);
      }
    }
  }

  function applyPincodeDetailsToCheckout(details: PincodeDetails | null, expectedPincode: string) {
    if (!details) return;

    setCheckoutForm((prev) => {
      if (prev.pincode !== expectedPincode) return prev;

      const next = {
        ...prev,
        city: details.city || prev.city,
        state: details.state || prev.state,
        country: details.country || prev.country || 'India',
      };

      if (typeof window !== 'undefined') {
        window.localStorage.setItem(checkoutStorageKey, JSON.stringify(next));
      }

      return next;
    });
  }

  function handleCheckoutPincodeChange(value: string) {
    const normalizedPincode = value.replace(/[^0-9]/g, '').slice(0, 6);
    updateFormField('pincode', normalizedPincode);

    if (PINCODE_REGEX.test(normalizedPincode)) {
      fetchShippingQuote(undefined, normalizedPincode);
    }
  }

  function selectPaymentMethod(method: PaymentMethod) {
    if (method === paymentMethod) return;

    setPaymentMethod(method);
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(paymentMethodStorageKey, method);
    }

    // The shipping rate differs between COD and prepaid, so invalidate the
    // current quote and immediately fetch a fresh one if the pincode is
    // already valid — saves the user a manual click on step 1.
    setShippingQuote(null);
    if (typeof window !== 'undefined') {
      window.localStorage.removeItem(quoteStorageKey);
    }

    if (PINCODE_REGEX.test(checkoutForm.pincode)) {
      fetchShippingQuote(method, checkoutForm.pincode);
    }
  }

  const isFormValid =
    checkoutForm.customerName.trim().length > 0 &&
    MOBILE_REGEX.test(checkoutForm.mobileNumber) &&
    EMAIL_REGEX.test(checkoutForm.email) &&
    checkoutForm.addressLine1.trim().length > 0 &&
    checkoutForm.city.trim().length > 0 &&
    checkoutForm.state.trim().length > 0 &&
    checkoutForm.country.trim().length > 0 &&
    PINCODE_REGEX.test(checkoutForm.pincode);

  const hasQuoteForCurrentSelection = Boolean(
    shippingQuote &&
      shippingQuote.pincode === checkoutForm.pincode &&
      shippingQuote.paymentMethod === paymentMethod &&
      shippingQuote.variantId === selectedVariant?.id &&
      shippingQuote.quantity === quantity
  );

  const hasValidQuote = Boolean(hasQuoteForCurrentSelection && shippingQuote?.available);

  const shippingCharge = hasValidQuote && shippingQuote
    ? Math.round(shippingQuote.freeDelivery ? 0 : shippingQuote.shippingCharge)
    : 0;
  const quoteShippingCharge = shippingQuote
    ? Math.round(shippingQuote.freeDelivery ? 0 : shippingQuote.shippingCharge)
    : 0;
  const subtotal = Math.round(totalPrice);
  const grandTotal = subtotal + shippingCharge;

  function getAnalyticsItem() {
    if (!selectedVariant) {
      return null;
    }

    return {
      item_id: String(selectedVariant.id),
      item_name: selectedVariant.name,
      item_brand: 'Dr. Detoxer',
      item_category: 'Product',
      price: Number(selectedVariant.price),
      quantity: Number(quantity),
      currency: 'INR',
    };
  }

  useEffect(() => {
    if (!selectedVariant) return;
    if (trackedViewVariantRef.current === selectedVariant.id) return;

    const item = getAnalyticsItem();
    if (!item) return;

    trackViewItem({
      item_id: item.item_id,
      item_name: item.item_name,
      price: item.price,
      quantity: item.quantity,
      currency: item.currency,
      item_brand: item.item_brand,
      item_category: item.item_category,
    });
    trackedViewVariantRef.current = selectedVariant.id;
  }, [selectedVariant, quantity]);

  async function fetchShippingQuote(methodOverride?: PaymentMethod, pincodeOverride?: string) {
    setQuoteError('');

    // The override matters because selectPaymentMethod calls this synchronously
    // after setPaymentMethod — the state update hasn't been applied yet.
    const effectiveMethod = methodOverride ?? paymentMethod;
    const effectivePincode = pincodeOverride ?? checkoutForm.pincode;

    if (!PINCODE_REGEX.test(effectivePincode)) {
      setQuoteError(t('product.checkout.errors.invalidPincode'));
      setShippingQuote(null);
      return;
    }

    if (!selectedVariant?.id) {
      setQuoteError('Select a product variant before checking shipping.');
      setShippingQuote(null);
      return;
    }

    setIsQuoteLoading(true);

    try {
      const response = await fetch(`${apiBaseUrl}/shipping/quote`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          deliveryPostcode: effectivePincode,
          paymentMethod: effectiveMethod,
          variantId: selectedVariant.id,
          quantity,
        }),
      });

      const payload = await response.json();

      if (!response.ok) {
        throw new Error(payload.message || t('product.checkout.errors.quoteFailed'));
      }

      const pincodeDetails = normalizePincodeDetails(payload.pincodeDetails);

      const quote: ShippingQuote = {
        available: Boolean(payload.available),
        shippingCharge: Number(payload.shippingCharge) || 0,
        freeDelivery: Boolean(payload.freeDelivery),
        eta: typeof payload.eta === 'string' ? payload.eta : null,
        courierName: typeof payload.courierName === 'string' ? payload.courierName : null,
        pincode: effectivePincode,
        paymentMethod: effectiveMethod,
        variantId: selectedVariant.id,
        quantity,
        city: pincodeDetails?.city || (typeof payload.city === 'string' ? payload.city : ''),
        state: pincodeDetails?.state || (typeof payload.state === 'string' ? payload.state : ''),
        country: pincodeDetails?.country || (typeof payload.country === 'string' ? payload.country : 'India'),
        pincodeDetails,
      };

      applyPincodeDetailsToCheckout(quote.pincodeDetails, effectivePincode);
      setShippingQuote(quote);
      if (typeof window !== 'undefined') {
        window.localStorage.setItem(quoteStorageKey, JSON.stringify(quote));
      }
    } catch (error) {
      setShippingQuote(null);
      setQuoteError(error instanceof Error ? error.message : t('product.checkout.errors.quoteFailed'));
    } finally {
      setIsQuoteLoading(false);
    }
  }

  function goToStep(step: CheckoutStep) {
    const item = getAnalyticsItem();

    if (item && checkoutStep === 1 && step === 2 && hasValidQuote) {
      trackBeginCheckout({
        items: [item],
        currency: 'INR',
        value: grandTotal,
      });
    }

    if (item && checkoutStep === 2 && step === 3 && hasValidQuote && isFormValid) {
      trackAddPaymentInfo({
        items: [item],
        currency: 'INR',
        value: grandTotal,
        payment_type: paymentMethod === 'cod' ? 'cash_on_delivery' : 'prepaid',
      });
    }

    setCheckoutError('');
    setCheckoutStep(step);
  }

  function resetCheckout() {
    setCheckoutStep(1);
    setCheckoutError('');
    setOrderReceipt(null);
  }

  async function handleSubmitOrder(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();

    if (submitLockRef.current || isSubmittingOrder || orderReceipt) return;
    if (!selectedVariant) {
      setCheckoutError(t('product.errors.unableCreateOrder'));
      return;
    }
    if (!isFormValid) {
      setCheckoutError(t('product.errors.unableCreateOrder'));
      return;
    }
    if (!hasValidQuote || !shippingQuote) {
      setCheckoutError(t('product.checkout.errors.missingQuote'));
      return;
    }

    submitLockRef.current = true;
    setIsSubmittingOrder(true);
    setCheckoutError('');
    setOrderReceipt(null);

    const isPrepaid = paymentMethod === 'prepaid';
    let shouldReleaseLock = true;

    try {
      // Razorpay script is only required for prepaid; preload before
      // creating the order so we can fail fast if the CDN is blocked.
      if (isPrepaid) {
        const scriptLoaded = await loadRazorpayScript();
        if (!scriptLoaded || !window.Razorpay) {
          throw new Error('Unable to load Razorpay checkout. Please try again.');
        }
      }

      const effectiveShippingCharge = shippingQuote.freeDelivery ? 0 : shippingQuote.shippingCharge;
      const response = await fetch(`${apiBaseUrl}/orders`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...checkoutForm,
          variantId: selectedVariant.id,
          quantity,
          paymentMethod,
          shippingCharge: effectiveShippingCharge,
          deliveryEta: shippingQuote.eta || undefined,
          courierName: shippingQuote.courierName || undefined,
        }),
      });

      const payload = await response.json();

      if (!response.ok) {
        throw new Error(payload.message || t('product.errors.unableCreateOrder'));
      }

      const data = payload.data || {};

      // COD branch — receipt is shown immediately.
      if (!isPrepaid) {
        const item = getAnalyticsItem();
        if (item) {
          trackPurchase({
            transaction_id: String(data.orderNumber || data.orderId),
            value: Number(data.amount || grandTotal),
            shipping: Number(data.shippingCharge ?? effectiveShippingCharge),
            currency: 'INR',
            items: [item],
            affiliation: 'Dr. Detoxer Direct',
          });
        }

        setOrderReceipt({
          orderId: String(data.orderId),
          orderNumber: String(data.orderNumber || data.orderId),
          amount: Number(data.amount || grandTotal),
          shippingCharge: Number(data.shippingCharge ?? effectiveShippingCharge),
          paymentMethod: 'cod',
          eta: shippingQuote.eta,
        });
        setQuantity(1);
        return;
      }

      // Prepaid branch — open Razorpay modal.
      const razorpayKeyId = publicRazorpayKeyId || String(data.razorpayKeyId || '');
      if (!razorpayKeyId) {
        throw new Error('Razorpay key id is not configured.');
      }

      const paymentSession: PaymentSession = {
        orderId: String(data.orderId),
        orderNumber: String(data.orderNumber || data.orderId),
        amount: Number(data.amount || grandTotal),
        amountPaise: Number(data.amountPaise || grandTotal * 100),
        currency: String(data.currency || 'INR'),
        razorpayOrderId: String(data.razorpayOrderId || ''),
        razorpayKeyId,
      };

      const razorpay = new window.Razorpay!({
        key: paymentSession.razorpayKeyId,
        amount: paymentSession.amountPaise,
        currency: paymentSession.currency,
        name: 'Dr. Detoxer',
        description: `Order ${paymentSession.orderNumber}`,
        order_id: paymentSession.razorpayOrderId,
        prefill: {
          name: checkoutForm.customerName,
          email: checkoutForm.email,
          contact: checkoutForm.mobileNumber,
        },
        notes: {
          localOrderId: paymentSession.orderId,
          orderNumber: paymentSession.orderNumber,
        },
        theme: { color: '#2d6b4f' },
        modal: {
          ondismiss: () => {
            setCheckoutError('Payment was cancelled before completion.');
            setIsSubmittingOrder(false);
            submitLockRef.current = false;
          },
        },
        handler: async (paymentResponse: any) => {
          try {
            const verifyResponse = await fetch(`${apiBaseUrl}/orders/verify-payment`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                localOrderId: paymentSession.orderId,
                ...paymentResponse,
              }),
            });

            const verifyPayload = await verifyResponse.json();

            if (!verifyResponse.ok) {
              throw new Error(verifyPayload.message || t('product.checkout.errors.verifyFailed'));
            }

            setOrderReceipt({
              orderId: String(verifyPayload.data.orderId),
              orderNumber: String(verifyPayload.data.orderNumber || paymentSession.orderNumber),
              amount: Number(verifyPayload.data.amount || paymentSession.amount),
              shippingCharge: Number(
                verifyPayload.data?.shippingCharge ?? (shippingQuote.freeDelivery ? 0 : shippingQuote.shippingCharge)
              ),
              paymentMethod: 'prepaid',
              eta: shippingQuote.eta,
            });

            const item = getAnalyticsItem();
            if (item) {
              trackPurchase({
                transaction_id: String(
                  verifyPayload.data.orderNumber ||
                    verifyPayload.data.orderId ||
                    paymentSession.orderNumber
                ),
                value: Number(verifyPayload.data.amount || paymentSession.amount),
                shipping: Number(
                  verifyPayload.data?.shippingCharge ??
                    (shippingQuote.freeDelivery ? 0 : shippingQuote.shippingCharge)
                ),
                currency: String(verifyPayload.data.currency || paymentSession.currency || 'INR'),
                items: [item],
                affiliation: 'Dr. Detoxer Direct',
              });
            }

            setQuantity(1);
          } catch (error) {
            setCheckoutError(error instanceof Error ? error.message : t('product.checkout.errors.verifyFailed'));
          } finally {
            setIsSubmittingOrder(false);
            submitLockRef.current = false;
          }
        },
      });

      razorpay.on('payment.failed', (failureResponse) => {
        setCheckoutError(failureResponse?.error?.description || t('product.errors.paymentFailed'));
        setIsSubmittingOrder(false);
        submitLockRef.current = false;
      });

      shouldReleaseLock = false;
      razorpay.open();
    } catch (error) {
      setCheckoutError(error instanceof Error ? error.message : t('product.errors.unableCreateOrder'));
    } finally {
      if (shouldReleaseLock) {
        setIsSubmittingOrder(false);
        submitLockRef.current = false;
      }
    }
  }

  return (
    <section
      id="product"
      ref={ref}
      // FIX 1: Removed `overflow-hidden`. Using `overflow-x-clip` prevents horizontal scrolling 
      // from the blur effect without clipping vertical content like modals/dropdowns.
      // FIX 2: Reduced mobile padding (`py-16` instead of `py-32`) for better screen real-estate.
      className="py-16 lg:py-16 bg-surface overflow-x-clip"
      aria-labelledby="product-heading">

      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <motion.div
          className="text-center mb-10 lg:mb-16"
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7 }}>

          <span className="text-xs font-semibold tracking-widest uppercase text-muted">
            {t('product.label')}
          </span>
        </motion.div>

        <div className="grid grid-cols-1 gap-12 lg:grid-cols-2 lg:gap-20 items-center">

          {/* Left: Product Image Slider (UPDATED) */}
          {/* Left: Product Image Slider (UPDATED) */}
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
            className="flex flex-col space-y-6  lg:overflow-visible"
          >
            {/* Main Image with 3D Tilt Wrapper */}
            <motion.div
              ref={imageRef}
              className="relative lg:cursor-none max-w-md mx-auto w-full lg:max-w-none"
            >
              <div
                className="absolute inset-0 m-auto rounded-full opacity-25 blur-3xl pointer-events-none"
                style={{
                  width: '80%',
                  height: '80%',
                  background: 'radial-gradient(circle, #2d6b4f 0%, #2a7fbf 60%, transparent 80%)'
                }}
                aria-hidden="true" 
              />

              <div
                className="relative rounded-4xl lg:rounded-5xl overflow-hidden shadow-green-lg bg-white border border-border aspect-square flex items-center justify-center p-6 cursor-pointer"
                onClick={() => setIsMediaModalOpen(true)}
                role="button"
                tabIndex={0}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    setIsMediaModalOpen(true);
                  }
                }}
                aria-label={mainMedia?.type === 'video' ? 'Open product video' : 'Open product image'}
              >
                {mainMedia?.type === 'video' ? (
                  <iframe
                    key={mainMedia.id}
                    width="560"
                    height="315"
                    src={mainMedia.src}
                    title="YouTube video player"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    referrerPolicy="strict-origin-when-cross-origin"
                    allowFullScreen
                    className="w-full h-full"
                  />
                ) : (
                  <AppImage
                    key={mainMedia?.id} // Forces React to update the image when clicked
                    src={mainMedia?.src || defaultImages[0].src}
                    alt={mainMedia?.alt || defaultImages[0].alt}
                    width={700}
                    height={700}
                    className="w-full h-full object-contain"
                  />
                )}
                <div className="absolute inset-0 pointer-events-none" />
              </div>

              {/* Floating Badges */}
              <motion.div
                className="absolute -top-8 -right-2 lg:-right-4 glass px-4 lg:px-5 py-2 lg:py-3 rounded-2xl lg:rounded-3xl shadow-medium text-center z-10"
                animate={{ y: [0, -8, 0] }}
                transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}>
                <p className="font-display text-xl lg:text-2xl font-semibold text-primary">{selectedVariant?.name || '500ml'}</p>
                <p className="text-muted text-[10px] lg:text-xs">{t('product.badges.premiumBottle')}</p>
              </motion.div>
              
              <motion.div
                className="absolute -bottom-4 -left-2 lg:-left-4 glass px-4 lg:px-5 py-2 lg:py-3 rounded-2xl lg:rounded-3xl shadow-medium z-10"
                animate={{ y: [0, 8, 0] }}
                transition={{ duration: 5, repeat: Infinity, ease: 'easeInOut', delay: 1.5 }}>
                <p className="text-[10px] lg:text-xs font-semibold text-primary">✨ {t('product.badges.labTested')}</p>
              </motion.div>
            </motion.div>

            {/* Thumbnail Navigation */}
            <div className="flex items-center justify-center gap-2 sm:gap-4 z-10 w-full max-w-[100vw]">
              <button
                type="button"
                onClick={handlePrev}
                disabled={currentIndex === 0}
                className="hidden sm:flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-border bg-white text-foreground disabled:opacity-50 hover:bg-surface transition-colors"
                aria-label="Previous image"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>

              {/* Custom Scrollbar Styling added here via arbitrary Tailwind classes */}
              <div className="flex gap-3 overflow-x-auto px-2 pb-3 pt-1 snap-x snap-mandatory w-full sm:w-auto [&::-webkit-scrollbar]:h-1.5 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-green-900/30 hover:[&::-webkit-scrollbar-thumb]:bg-green-900/50 [&::-webkit-scrollbar-thumb]:rounded-full">
                {currentMedia.map((item, idx) => {
                  const isVisibleDesktop = idx >= currentIndex && idx < currentIndex + thumbnailCount;

                  return (
                    <div
                      key={item.id}
                      className={`flex-shrink-0 snap-center w-20 h-20 sm:w-20 sm:h-20 relative cursor-pointer rounded-2xl overflow-hidden border-2 transition-all bg-white ${
                        !isVisibleDesktop ? 'sm:hidden' : ''
                      } ${
                        mainMedia.id === item.id
                          ? "border-primary ring-2 ring-primary/20 scale-105"
                          : "border-border hover:border-primary/50"
                      }`}
                      onMouseEnter={() => setMainMedia(item)}
                      onClick={() => setMainMedia(item)}
                    >
                      {item.type === 'video' ? (
                        <>
                          <video
                            src={item.src}
                            muted
                            playsInline
                            preload="metadata"
                            className="w-full h-full object-cover"
                          />
                          <div className="absolute inset-0 flex items-center justify-center bg-black/25">
                            <Play
                              className="w-6 h-6 text-white drop-shadow"
                              fill="white"
                              strokeWidth={1.5}
                            />
                          </div>
                        </>
                      ) : (
                        <AppImage
                          src={item.src}
                          alt={item.alt}
                          width={80}
                          height={80}
                          className="w-full h-full object-contain p-2"
                        />
                      )}
                    </div>
                  );
                })}
              </div>

              <button
                type="button"
                onClick={handleNext}
                disabled={currentIndex >= currentMedia.length - thumbnailCount}
                className="hidden sm:flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-border bg-white text-foreground disabled:opacity-50 hover:bg-surface transition-colors"
                aria-label="Next image"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </motion.div>

          {/* Right: Purchase UI (UNTOUCHED) */}
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 1, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}>

            <div className="space-y-6 lg:space-y-8">
              <div>
                <span className="text-xs tracking-widest uppercase text-muted font-semibold block mb-2 lg:mb-3">
                  {t('product.brand')}
                </span>
                <h2
                  id="product-heading"
                  // FIX 4: Adjusted heading sizes for mobile
                  className="font-display text-3xl sm:text-4xl lg:text-5xl font-light text-foreground leading-tight">

                  {t('product.title.line1')}
                  <br />
                  <span className="italic pl-0.5 pr-2 text-gradient-green">{t('product.title.line2')}</span>
                </h2>

                <ProductJsonLd
                  id={selectedVariant?.id || 'dr-detoxer-product'}
                  name={`${t('product.title.line1')} ${t('product.title.line2')}`}
                  description={t('hero.description') || `${t('product.title.line1')} ${t('product.title.line2')}`}
                  image={currentImages[0]?.src || '/assets/images/only_bottel.png'}
                  sku={selectedVariant?.id}
                  brand={t('product.brand')}
                  price={selectedPrice}
                  currency={'INR'}
                  availability={selectedVariant && selectedVariant.stock > 0 ? 'InStock' : 'OutOfStock'}
                  url={`${SITE_URL}/product/${selectedVariant?.id || 'dr-detoxer'}`}
                />
              </div>

              <div className="flex items-baseline gap-3 lg:gap-4">
                <span className="font-display text-4xl lg:text-5xl font-semibold text-foreground">₹{selectedPrice}</span>
                <span className="text-muted line-through text-lg lg:text-xl">₹{selectedMrpPrice}</span>
                <span className="bg-primary text-white text-[10px] lg:text-xs px-2.5 py-1 lg:px-3 lg:py-1.5 rounded-full font-semibold">
                  {selectedDiscountPercentage}% off
                </span>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {highlights.map((h) =>
                  <div
                    key={h.text}
                    className="flex items-center gap-2.5 bg-white rounded-2xl px-4 py-3 border border-border shadow-sm">
                    <span className="text-lg lg:text-xl">{h.icon}</span>
                    <span className="text-foreground text-sm font-medium">{h.text}</span>
                  </div>
                )}
              </div>

              <div>
                <p className="text-sm font-semibold text-foreground mb-2">{t('product.variant')}</p>
                <div className="mb-5 relative">
                  <select
                    value={selectedVariantId}
                    onChange={(event) => setSelectedVariantId(event.target.value)}
                    className="w-full rounded-2xl border border-border bg-white px-4 py-3 pr-12 text-sm font-medium text-foreground outline-none focus:border-primary focus:ring-1 focus:ring-primary appearance-none"
                    aria-label={t('product.selectVariantAria')}
                  >
                    {variants.map((variant) => (
                      <option key={variant.id} value={variant.id}>
                        {variant.name} — ₹{variant.price}
                      </option>
                    ))}
                  </select>
                  <FaAngleDown className="pointer-events-none absolute right-4 top-1/2 h-4 w-4 -translate-y-1/2 text-muted" aria-hidden="true" />
                </div>

                <p className="text-sm font-semibold text-foreground mb-2">{t('product.quantity')}</p>
                <div className="flex flex-wrap items-center gap-4">
                  <div className="flex items-center border border-border rounded-2xl overflow-hidden bg-white">
                    <button
                      onClick={() => setQuantity(Math.max(1, quantity - 1))}
                      className="w-10 h-10 lg:w-12 lg:h-12 flex items-center justify-center text-foreground font-bold text-lg border-r border-border hover:bg-surface transition-colors"
                      aria-label={t('product.decreaseQuantity')}>
                      −
                    </button>
                    <span className="w-12 lg:w-14 text-center font-semibold text-foreground">
                      {quantity}
                    </span>
                    <button
                      onClick={() => setQuantity(quantity + 1)}
                      className="w-10 h-10 lg:w-12 lg:h-12 flex items-center justify-center text-foreground font-bold text-lg border-l border-border hover:bg-surface transition-colors"
                      aria-label={t('product.increaseQuantity')}>
                      +
                    </button>
                  </div>
                  <span className="text-muted text-sm">
                    {t('product.total')}:{' '}
                    <span className="font-semibold text-foreground">₹{totalPrice}</span>
                  </span>
                </div>
              </div>

              <button
                type="button"
                onClick={() => {
                  const item = getAnalyticsItem();
                  if (item) {
                    trackAddToCart({
                      item_id: item.item_id,
                      item_name: item.item_name,
                      price: item.price,
                      quantity: item.quantity,
                      currency: item.currency,
                      item_brand: item.item_brand,
                      item_category: item.item_category,
                      cart_value: item.price * item.quantity,
                    });
                  }

                  setIsCartOpen(true);
                  setCheckoutError('');
                  setOrderReceipt(null);
                  // Skip step 1 if a fresh quote is already in localStorage.
                  setCheckoutStep(hasValidQuote ? 2 : 1);
                }}
                disabled={!selectedVariant || selectedVariant.stock <= 0}
                className="btn-primary w-full py-4 lg:py-5 rounded-2xl font-semibold text-base lg:text-lg tracking-wide shadow-green-lg transition-transform active:scale-[0.98]"
                aria-label={`Buy ${quantity} ${selectedVariant?.name || 'Dr. Detoxer'} for ₹${totalPrice}`}>

                {selectedVariant && selectedVariant.stock > 0 ? `${t('common:actions.buyNow')} — ₹${totalPrice}` : t('product.outOfStock')}
              </button>

              <div className="flex flex-wrap gap-4 lg:gap-6 text-xs lg:text-sm text-muted justify-center sm:justify-start">
                <span className="flex items-center gap-1.5"><span>🔒</span> {t('product.trust.secureCheckout')}</span>
                <span className="flex items-center gap-1.5"><span>🚚</span> {t('product.trust.fastDelivery')}</span>
                <span className="flex items-center gap-1.5"><span>📞</span> {t('finalCta.trust.support')}</span>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* FIX 5: Completely restructured Cart Drawer for proper mobile scrolling & safe area behavior */}
      {isCartOpen ? (
        <div className="fixed inset-0 z-50 flex justify-end bg-black/50 backdrop-blur-sm" role="dialog" aria-modal="true" aria-label={t('product.checkout.title')}>
          <button
            type="button"
            aria-label={t('product.checkout.closeBackdrop')}
            onClick={() => setIsCartOpen(false)}
            className="absolute inset-0 w-full h-full cursor-default"
          />

          <aside className="relative w-full sm:w-[500px] h-full bg-white shadow-strong flex flex-col transform transition-transform duration-300">
            {/* Fixed Header */}
            <header className="flex-shrink-0 flex items-center justify-between border-b border-border bg-white px-5 py-4">
              <h3 className="font-display text-xl lg:text-2xl text-foreground">{t('product.checkout.title')}</h3>
              <button
                type="button"
                onClick={() => setIsCartOpen(false)}
                className="rounded-full border border-border px-4 py-2 text-sm font-semibold text-foreground hover:bg-surface transition-colors"
                aria-label="Close"
              >
                <X className="w-4 h-4" />
              </button>
            </header>

            {/* Stepper — hidden once the receipt is shown */}
            {!orderReceipt ? (
              <div className="flex-shrink-0 px-5 pt-4 sm:px-6">
                <ol className="flex items-center gap-2">
                  {([1, 2, 3] as const).map((step, idx) => {
                    const stepLabels = [
                      t('product.checkout.steps.shipping'),
                      t('product.checkout.steps.details'),
                      t('product.checkout.steps.review'),
                    ];
                    const isActive = checkoutStep === step;
                    const isDone = checkoutStep > step;
                    return (
                      <React.Fragment key={step}>
                        <li className="flex items-center gap-2">
                          <span
                            className={`flex h-7 w-7 items-center justify-center rounded-full text-xs font-bold transition-colors ${
                              isActive
                                ? 'bg-primary text-white'
                                : isDone
                                  ? 'bg-green-100 text-green-700 border border-green-300'
                                  : 'bg-surface text-muted border border-border'
                            }`}
                          >
                            {isDone ? '✓' : step}
                          </span>
                          <span
                            className={`text-xs font-semibold whitespace-nowrap ${
                              isActive ? 'text-foreground' : 'text-muted'
                            } hidden sm:inline`}
                          >
                            {stepLabels[idx]}
                          </span>
                        </li>
                        {step < 3 ? (
                          <span className={`h-px flex-1 ${checkoutStep > step ? 'bg-green-300' : 'bg-border'}`} />
                        ) : null}
                      </React.Fragment>
                    );
                  })}
                </ol>
              </div>
            ) : null}

            {/* Scrollable content */}
            <div className="flex-1 overflow-y-auto p-5 sm:p-6 pb-24">
              <div className="mb-5 rounded-2xl border border-border bg-surface p-4">
                <p className="text-xs lg:text-sm text-muted">{t('product.checkout.selected')}</p>
                <p className="mt-1 text-sm lg:text-base font-semibold text-foreground">
                  {selectedVariant?.name} × {quantity}
                </p>
                <div className="mt-2 flex items-center justify-between text-xs text-muted">
                  <span>{t('product.checkout.payment.subtotal')}</span>
                  <span className="font-semibold text-foreground">₹{subtotal}</span>
                </div>
                {hasValidQuote ? (
                  <div className="mt-1 flex items-center justify-between text-xs text-muted">
                    <span>{t('product.checkout.payment.summary')} · {t('product.checkout.shipping.fee')}</span>
                    <span className="font-semibold text-foreground">
                      {shippingCharge === 0 ? t('product.checkout.shipping.free') : `₹${shippingCharge}`}
                    </span>
                  </div>
                ) : null}
                <div className="mt-2 flex items-center justify-between border-t border-border pt-2 text-sm">
                  <span className="font-semibold text-foreground">{t('product.total')}</span>
                  <span className="font-bold text-foreground">₹{grandTotal}</span>
                </div>
              </div>

              {orderReceipt ? (
                <motion.div
                  initial={{ opacity: 0, y: 16, scale: 0.96 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  transition={{ duration: 0.35 }}
                  className="rounded-2xl border border-green-200 bg-green-50 p-5"
                >
                  <div className="mb-4 flex items-center gap-3">
                    <motion.div
                      initial={{ scale: 0.8 }}
                      animate={{ scale: [1, 1.08, 1] }}
                      transition={{ duration: 0.6 }}
                      className="flex h-12 w-12 items-center justify-center rounded-full bg-green-600 text-2xl text-white"
                    >
                      ✓
                    </motion.div>
                    <div>
                      <p className="text-base font-semibold text-green-800">{t('product.checkout.receipt.title')}</p>
                      <p className="text-xs text-green-700">{t('product.checkout.orderPlaced')}</p>
                    </div>
                  </div>

                  <dl className="space-y-2 rounded-xl bg-white p-3 text-sm">
                    <div className="flex justify-between">
                      <dt className="text-muted">{t('product.checkout.orderNumber')}</dt>
                      <dd className="font-semibold text-foreground">{orderReceipt.orderNumber}</dd>
                    </div>
                    <div className="flex justify-between">
                      <dt className="text-muted">{t('product.checkout.amount')}</dt>
                      <dd className="font-semibold text-foreground">₹{orderReceipt.amount}</dd>
                    </div>
                    <div className="flex justify-between">
                      <dt className="text-muted">{t('product.checkout.receipt.shipping')}</dt>
                      <dd className="font-semibold text-foreground">
                        {orderReceipt.shippingCharge === 0
                          ? t('product.checkout.shipping.free')
                          : `₹${orderReceipt.shippingCharge}`}
                      </dd>
                    </div>
                    <div className="flex justify-between">
                      <dt className="text-muted">{t('product.checkout.receipt.paymentMethod')}</dt>
                      <dd className="font-semibold text-foreground">
                        {orderReceipt.paymentMethod === 'cod'
                          ? t('product.checkout.receipt.cod')
                          : t('product.checkout.receipt.prepaid')}
                      </dd>
                    </div>
                    {orderReceipt.eta ? (
                      <div className="flex justify-between">
                        <dt className="text-muted">{t('product.checkout.shipping.etaPrefix')}</dt>
                        <dd className="font-semibold text-foreground">{orderReceipt.eta}</dd>
                      </div>
                    ) : null}
                  </dl>

                  <div className="mt-4 flex flex-col gap-2 sm:flex-row">
                    <button
                      type="button"
                      onClick={() => {
                        if (typeof window !== 'undefined') {
                          window.location.href = `/home/track-order?type=order&query=${encodeURIComponent(orderReceipt.orderNumber)}`;
                        }
                      }}
                      className="flex-1 rounded-xl border border-green-700 bg-white px-3 py-2.5 text-sm font-semibold text-green-800 hover:bg-green-50 transition-colors"
                    >
                      {t('product.checkout.trackOrder')}
                    </button>
                    <button
                      type="button"
                      onClick={resetCheckout}
                      className="flex-1 rounded-xl border border-green-200 bg-green-100 px-3 py-2.5 text-sm font-semibold text-green-800 hover:bg-green-200 transition-colors"
                    >
                      {t('product.checkout.placeAnother')}
                    </button>
                  </div>
                </motion.div>
              ) : (
                <form className="space-y-4" onSubmit={handleSubmitOrder}>
                  {/* STEP 1 — Payment method + pincode + shipping quote */}
                  {checkoutStep === 1 ? (
                    <div className="space-y-5">
                      <div>
                        <h4 className="font-display text-lg text-foreground">{t('product.checkout.shipping.heading')}</h4>
                        <p className="mt-1 text-xs text-muted">{t('product.checkout.shipping.subheading')}</p>
                      </div>

                      {/* Payment method picker — moved up so the quote is fetched
                          for the right rate without an extra trip to step 3. */}
                      <div>
                        <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted">
                          {t('product.checkout.payment.heading')}
                        </p>
                        <div className="grid gap-2 sm:grid-cols-2">
                          {(['prepaid', 'cod'] as PaymentMethod[]).map((method) => {
                            const isSelected = paymentMethod === method;
                            return (
                              <button
                                key={method}
                                type="button"
                                onClick={() => selectPaymentMethod(method)}
                                aria-pressed={isSelected}
                                className={`flex w-full items-start gap-3 rounded-2xl border p-3 text-left transition-colors ${
                                  isSelected
                                    ? 'border-primary bg-primary/5 ring-1 ring-primary'
                                    : 'border-border bg-white hover:border-primary/40'
                                }`}
                              >
                                <span
                                  className={`mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full border-2 ${
                                    isSelected ? 'border-primary' : 'border-border'
                                  }`}
                                >
                                  {isSelected ? <span className="h-2 w-2 rounded-full bg-primary" /> : null}
                                </span>
                                <span className="flex-1">
                                  <span className="block text-sm font-semibold text-foreground">
                                    {method === 'prepaid'
                                      ? t('product.checkout.payment.prepaidLabel')
                                      : t('product.checkout.payment.codLabel')}
                                  </span>
                                  <span className="mt-0.5 block text-xs text-muted">
                                    {method === 'prepaid'
                                      ? t('product.checkout.payment.prepaidHint')
                                      : t('product.checkout.payment.codHint')}
                                  </span>
                                </span>
                              </button>
                            );
                          })}
                        </div>
                      </div>

                      <div>
                        <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted">
                          {t('product.checkout.fields.pincode')}
                        </p>
                        <div className="flex gap-2">
                          <input
                            type="tel"
                            inputMode="numeric"
                            value={checkoutForm.pincode}
                            onChange={(event) => handleCheckoutPincodeChange(event.target.value)}
                            autoComplete="postal-code"
                            className="w-full rounded-2xl border border-border bg-white px-4 py-3 text-base text-foreground outline-none focus:border-primary focus:ring-1 focus:ring-primary"
                            placeholder={t('product.checkout.shipping.pincodePlaceholder')}
                            aria-label={t('product.checkout.fields.pincode')}
                          />
                          <button
                            type="button"
                            onClick={() => fetchShippingQuote()}
                            disabled={isQuoteLoading || !PINCODE_REGEX.test(checkoutForm.pincode)}
                            className="btn-primary shrink-0 rounded-2xl px-4 py-3 text-sm font-semibold disabled:cursor-not-allowed disabled:opacity-60"
                          >
                            {isQuoteLoading ? t('product.checkout.shipping.loading') : t('product.checkout.shipping.checkButton')}
                          </button>
                        </div>
                      </div>

                      {quoteError ? (
                        <p className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm font-medium text-red-700">
                          {quoteError}
                        </p>
                      ) : null}

                      {hasQuoteForCurrentSelection && shippingQuote ? (
                        shippingQuote.available ? (
                          <div className="space-y-2">
                            <div className="rounded-2xl border border-green-200 bg-green-50 p-4">
                              <p className="text-sm font-semibold text-green-800">
                                ✓ {t('product.checkout.shipping.available')}
                              </p>
                              <div className="mt-2 grid grid-cols-2 gap-2 text-xs text-green-900">
                                <div>
                                  <p className="text-green-700">{t('product.checkout.shipping.fee')}</p>
                                  <p className="font-bold">
                                    {quoteShippingCharge === 0
                                      ? t('product.checkout.shipping.free')
                                      : `₹${quoteShippingCharge}`}
                                  </p>
                                </div>
                                {shippingQuote.eta ? (
                                  <div>
                                    <p className="text-green-700">{t('product.checkout.shipping.etaPrefix')}</p>
                                    <p className="font-bold">{shippingQuote.eta}</p>
                                  </div>
                                ) : null}
                              </div>
                              {shippingQuote.courierName ? (
                                <p className="mt-2 text-xs text-green-700">
                                  {t('product.checkout.shipping.courier')}: {shippingQuote.courierName}
                                </p>
                              ) : null}
                            </div>
                            <p className="px-1 text-[11px] text-muted leading-snug">
                              ⓘ {t('product.checkout.shipping.estimatedNote')}
                            </p>
                          </div>
                        ) : (
                          <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm font-medium text-amber-800">
                            {t('product.checkout.shipping.unavailable')}
                          </div>
                        )
                      ) : null}

                      <button
                        type="button"
                        onClick={() => goToStep(2)}
                        disabled={!hasValidQuote}
                        className="btn-primary w-full rounded-2xl py-3.5 text-sm font-semibold disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        {t('product.checkout.shipping.continue')}
                      </button>
                    </div>
                  ) : null}

                  {/* STEP 2 — Customer + address details */}
                  {checkoutStep === 2 ? (
                    <div className="space-y-4">
                      <div>
                        <h4 className="font-display text-lg text-foreground">{t('product.checkout.details.heading')}</h4>
                        <p className="mt-1 text-xs text-muted">{t('product.checkout.details.subheading')}</p>
                      </div>

                      <input
                        type="text"
                        value={checkoutForm.customerName}
                        onChange={(event) => updateFormField('customerName', event.target.value)}
                        autoComplete="name"
                        className="w-full rounded-2xl border border-border bg-white px-4 py-3 text-sm text-foreground outline-none focus:border-primary focus:ring-1 focus:ring-primary"
                        placeholder={t('product.checkout.fields.fullName')}
                        required
                      />

                      <div className="grid gap-4 sm:grid-cols-2">
                        <input
                          type="tel"
                          inputMode="numeric"
                          value={checkoutForm.mobileNumber}
                          onChange={(event) =>
                            updateFormField(
                              'mobileNumber',
                              event.target.value.replace(/[^0-9]/g, '').slice(0, 10)
                            )
                          }
                          autoComplete="tel-national"
                          className="w-full rounded-2xl border border-border bg-white px-4 py-3 text-sm text-foreground outline-none focus:border-primary focus:ring-1 focus:ring-primary"
                          placeholder={t('product.checkout.fields.mobileNumber')}
                          required
                        />
                        <input
                          type="email"
                          value={checkoutForm.email}
                          onChange={(event) => updateFormField('email', event.target.value)}
                          autoComplete="email"
                          className="w-full rounded-2xl border border-border bg-white px-4 py-3 text-sm text-foreground outline-none focus:border-primary focus:ring-1 focus:ring-primary"
                          placeholder={t('product.checkout.fields.emailId')}
                          required
                        />
                      </div>

                      <input
                        type="text"
                        value={checkoutForm.addressLine1}
                        onChange={(event) => updateFormField('addressLine1', event.target.value)}
                        autoComplete="address-line1"
                        className="w-full rounded-2xl border border-border bg-white px-4 py-3 text-sm text-foreground outline-none focus:border-primary focus:ring-1 focus:ring-primary"
                        placeholder={t('product.checkout.fields.addressLine1')}
                        required
                      />

                      <input
                        type="text"
                        value={checkoutForm.addressLine2}
                        onChange={(event) => updateFormField('addressLine2', event.target.value)}
                        autoComplete="address-line2"
                        className="w-full rounded-2xl border border-border bg-white px-4 py-3 text-sm text-foreground outline-none focus:border-primary focus:ring-1 focus:ring-primary"
                        placeholder={t('product.checkout.fields.addressLine2')}
                      />

                      <input
                        type="text"
                        value={checkoutForm.landmark}
                        onChange={(event) => updateFormField('landmark', event.target.value)}
                        className="w-full rounded-2xl border border-border bg-white px-4 py-3 text-sm text-foreground outline-none focus:border-primary focus:ring-1 focus:ring-primary"
                        placeholder={t('product.checkout.fields.landmark')}
                      />

                      <div className="grid gap-4 sm:grid-cols-2">
                        <input
                          type="text"
                          value={checkoutForm.city}
                          onChange={(event) => updateFormField('city', event.target.value)}
                          autoComplete="address-level2"
                          className="w-full rounded-2xl border border-border bg-white px-4 py-3 text-sm text-foreground outline-none focus:border-primary focus:ring-1 focus:ring-primary"
                          placeholder={t('product.checkout.fields.city')}
                          required
                        />
                        <input
                          type="text"
                          value={checkoutForm.state}
                          onChange={(event) => updateFormField('state', event.target.value)}
                          autoComplete="address-level1"
                          className="w-full rounded-2xl border border-border bg-white px-4 py-3 text-sm text-foreground outline-none focus:border-primary focus:ring-1 focus:ring-primary"
                          placeholder={t('product.checkout.fields.state')}
                          required
                        />
                        <input
                          type="text"
                          value={checkoutForm.pincode}
                          onChange={(event) => handleCheckoutPincodeChange(event.target.value)}
                          autoComplete="postal-code"
                          className="w-full rounded-2xl border border-border bg-white px-4 py-3 text-sm text-foreground outline-none focus:border-primary focus:ring-1 focus:ring-primary"
                          placeholder={t('product.checkout.fields.pincode')}
                          required
                        />
                        <input
                          type="text"
                          value={checkoutForm.country}
                          onChange={(event) => updateFormField('country', event.target.value)}
                          autoComplete="country-name"
                          className="w-full rounded-2xl border border-border bg-white px-4 py-3 text-sm text-foreground outline-none focus:border-primary focus:ring-1 focus:ring-primary"
                          placeholder="Country"
                          required
                        />
                      </div>

                      <div className="flex gap-2 pt-2">
                        <button
                          type="button"
                          onClick={() => goToStep(1)}
                          className="rounded-2xl border border-border bg-white px-4 py-3 text-sm font-semibold text-foreground hover:bg-surface transition-colors"
                        >
                          {t('product.checkout.details.back')}
                        </button>
                        <button
                          type="button"
                          onClick={() => goToStep(3)}
                          disabled={!isFormValid || !hasValidQuote}
                          className="btn-primary flex-1 rounded-2xl py-3 text-sm font-semibold disabled:cursor-not-allowed disabled:opacity-60"
                        >
                          {t('product.checkout.details.continueToPayment')}
                        </button>
                      </div>
                    </div>
                  ) : null}

                  {/* STEP 3 — Review + place order */}
                  {checkoutStep === 3 ? (
                    <div className="space-y-4">
                      <div>
                        <h4 className="font-display text-lg text-foreground">{t('product.checkout.review.heading')}</h4>
                        <p className="mt-1 text-xs text-muted">{t('product.checkout.review.subheading')}</p>
                      </div>

                      {/* Delivery address summary */}
                      <div className="rounded-2xl border border-border bg-white p-4">
                        <div className="flex items-start justify-between gap-2">
                          <p className="text-xs font-semibold uppercase tracking-wide text-muted">
                            {t('product.checkout.review.deliveringTo')}
                          </p>
                          <button
                            type="button"
                            onClick={() => goToStep(2)}
                            className="text-xs font-semibold text-primary hover:underline"
                          >
                            {t('product.checkout.review.back')}
                          </button>
                        </div>
                        <p className="mt-2 text-sm font-semibold text-foreground">{checkoutForm.customerName}</p>
                        <p className="text-xs text-muted">
                          {checkoutForm.mobileNumber} · {checkoutForm.email}
                        </p>
                        <p className="mt-1 text-xs text-foreground">
                          {[
                            checkoutForm.addressLine1,
                            checkoutForm.addressLine2,
                            checkoutForm.landmark,
                            checkoutForm.city,
                            checkoutForm.state,
                            checkoutForm.pincode,
                            checkoutForm.country,
                          ]
                            .filter(Boolean)
                            .join(', ')}
                        </p>
                      </div>

                      {/* Payment method summary */}
                      <div className="flex items-center justify-between rounded-2xl border border-border bg-white p-4">
                        <div>
                          <p className="text-xs font-semibold uppercase tracking-wide text-muted">
                            {t('product.checkout.review.paymentMethod')}
                          </p>
                          <p className="mt-1 text-sm font-semibold text-foreground">
                            {paymentMethod === 'cod'
                              ? t('product.checkout.payment.codLabel')
                              : t('product.checkout.payment.prepaidLabel')}
                          </p>
                          <p className="mt-0.5 text-xs text-muted">
                            {paymentMethod === 'cod'
                              ? t('product.checkout.payment.codHint')
                              : t('product.checkout.payment.prepaidHint')}
                          </p>
                        </div>
                        <button
                          type="button"
                          onClick={() => goToStep(1)}
                          className="text-xs font-semibold text-primary hover:underline"
                        >
                          {t('product.checkout.review.back')}
                        </button>
                      </div>

                      {/* Quote sanity guard — should be impossible to reach without
                          a valid quote, but keep a graceful fallback. */}
                      {!hasValidQuote ? (
                        <div className="rounded-2xl border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
                          <p className="mb-2 font-medium">
                            {isQuoteLoading
                              ? t('product.checkout.shipping.loading')
                              : t('product.checkout.errors.missingQuote')}
                          </p>
                          <button
                            type="button"
                            onClick={() => fetchShippingQuote()}
                            disabled={isQuoteLoading || !PINCODE_REGEX.test(checkoutForm.pincode)}
                            className="rounded-xl border border-amber-300 bg-white px-3 py-1.5 text-xs font-semibold text-amber-900 disabled:opacity-60"
                          >
                            {t('product.checkout.shipping.retry')}
                          </button>
                        </div>
                      ) : null}

                      {checkoutError ? (
                        <p className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm font-medium text-red-700">
                          {checkoutError}
                        </p>
                      ) : null}

                      <p className="px-1 text-[11px] text-muted leading-snug">
                        ⓘ {t('product.checkout.shipping.estimatedNote')}
                      </p>

                      <div className="flex gap-2 pt-2">
                        <button
                          type="button"
                          onClick={() => goToStep(2)}
                          className="rounded-2xl border border-border bg-white px-4 py-3 text-sm font-semibold text-foreground hover:bg-surface transition-colors"
                        >
                          {t('product.checkout.review.back')}
                        </button>
                        <button
                          type="submit"
                          disabled={isSubmittingOrder || !hasValidQuote || !isFormValid}
                          className="btn-primary flex-1 rounded-2xl py-3 text-sm font-semibold disabled:cursor-not-allowed disabled:opacity-60 active:scale-[0.98] transition-transform"
                        >
                          {isSubmittingOrder
                            ? t('product.checkout.processing')
                            : `${
                                paymentMethod === 'cod'
                                  ? t('product.checkout.placeCodOrder')
                                  : t('product.checkout.payNow')
                              } — ₹${grandTotal}`}
                        </button>
                      </div>
                    </div>
                  ) : null}
                </form>
              )}
            </div>
          </aside>
        </div>
      ) : null}

      {/* ═══════════════════ MEDIA MODAL DIALOG ═══════════════════ */}
      {isMediaModalOpen ? (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 p-4 backdrop-blur-sm"
          onClick={() => setIsMediaModalOpen(false)}
          role="dialog"
          aria-modal="true"
          aria-label={mainMedia?.type === 'video' ? 'Product video preview' : 'Product image preview'}
        >
          <button
            type="button"
            onClick={() => setIsMediaModalOpen(false)}
            className="absolute right-4 top-4 z-[110] flex h-10 w-10 items-center justify-center rounded-full bg-white/10 text-white transition hover:bg-white/25 focus:outline-none focus:ring-2 focus:ring-white/50 sm:right-8 sm:top-8"
            aria-label="Close modal"
          >
            <X className="h-6 w-6" />
          </button>

          <div
            className="relative flex max-h-full max-w-full items-center justify-center"
            onClick={(event) => event.stopPropagation()}
          >
            {mainMedia?.type === 'video' ? (
              <iframe
                width="560"
                height="315"
                src={mainMedia.src}
                title="YouTube video player"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                referrerPolicy="strict-origin-when-cross-origin"
                allowFullScreen
                className="max-h-[90vh] max-w-[90vw] rounded-xl shadow-2xl"
              />
            ) : (
              <img
                src={mainMedia?.src || defaultImages[0].src}
                alt={mainMedia?.alt || defaultImages[0].alt}
                className="max-h-[90vh] max-w-[90vw] rounded-xl object-contain shadow-2xl"
              />
            )}
          </div>
        </div>
      ) : null}
    </section>
  );
}
